import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, Search } from "lucide-react";

function dueDateInfo(dueDate) {
  if (!dueDate) return null;
  const days = Math.round((new Date(dueDate) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
  if (days < 0) return { label: `Overdue by ${Math.abs(days)}d`, cls: "text-red-600" };
  if (days <= 3) return { label: days === 0 ? "Due today" : `Due in ${days}d`, cls: "text-amber-600" };
  return { label: `Due ${dueDate}`, cls: "text-muted-foreground" };
}

const LIFECYCLE_CLS = {
  active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  onboarding: "border-sky-500/40 bg-sky-500/10 text-sky-600",
  offboarding: "border-amber-500/40 bg-amber-500/10 text-amber-600",
};

function PersonPoliciesDrawer({ person, onClose, isAdmin, onChanged }) {
  const { toast } = useToast();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/compliance/people/${person.id}/policies`);
      setPolicies(data.policies || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [person.id]);
  useEffect(() => { load(); }, [load]);

  const toggleSign = async (p) => {
    setBusyId(p.policy_id);
    try {
      await api.put(`/compliance/policies/${p.policy_id}/acknowledgments/${person.id}`, {
        status: p.status === "signed" ? "pending" : "signed",
      });
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't update sign-off", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyId(null); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-person-policies-drawer">
        <DialogHeader>
          <DialogTitle>{person.name} — Assigned policies</DialogTitle>
          <DialogDescription>Policies assigned to this person and their sign-off status.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1 max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="h-16 rounded-lg bg-muted/40 animate-pulse" />
          ) : policies.length === 0 ? (
            <div className="text-[12px] text-muted-foreground">No policies assigned to this person yet.</div>
          ) : (
            policies.map((p) => (
              <div key={p.policy_id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5" data-testid={`compliance-person-policy-row-${p.policy_id}`}>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium truncate">{p.policy_name}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{p.policy_code}</div>
                  {p.status === "pending" && dueDateInfo(p.due_date) && (
                    <span className={cn("text-[10.5px] font-medium", dueDateInfo(p.due_date).cls)} data-testid={`compliance-person-policy-due-${p.policy_id}`}>
                      {dueDateInfo(p.due_date).label}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={cn(
                    "text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5",
                    p.status === "signed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-amber-500/40 bg-amber-500/10 text-amber-600"
                  )}>{p.status === "signed" ? "Signed" : "Pending"}</span>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => toggleSign(p)}
                      disabled={busyId === p.policy_id}
                      className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                      data-testid={`compliance-person-policy-toggle-${p.policy_id}`}
                    >
                      {p.status === "signed" ? "Mark pending" : "Mark signed"}
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-person-policies-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddPersonModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || !email.trim()) { toast({ title: "Name and email are required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/people", { name, email, title, department });
      toast({ title: "Person added" });
      setName(""); setEmail(""); setTitle(""); setDepartment("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add person", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-person-modal">
        <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-person-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="compliance-person-email-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" data-testid="compliance-person-title-input" />
            </div>
            <div>
              <Label>Department</Label>
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} className="mt-1" data-testid="compliance-person-department-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-person-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-person-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompliancePeople({ isAdmin }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/people");
      setPeople(data.people || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => people.filter((p) => {
    if (lifecycleFilter !== "all" && p.lifecycle !== lifecycleFilter) return false;
    if (search && !`${p.name} ${p.email} ${p.title || ""} ${p.department || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [people, search, lifecycleFilter]);

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="compliance-people-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people…" className="pl-8 h-9" data-testid="compliance-people-search" />
        </div>
        <Select value={lifecycleFilter} onValueChange={setLifecycleFilter}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="compliance-people-lifecycle-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All lifecycles</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="offboarding">Offboarding</SelectItem>
          </SelectContent>
        </Select>
        {isAdmin && (
          <Button size="sm" className="gap-1.5 ml-auto" onClick={() => setModalOpen(true)} data-testid="compliance-person-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add team member
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-people-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Lifecycle</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow
                key={p.id}
                data-testid={`compliance-person-row-${p.id}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setSelected(p)}
              >
                <TableCell className="text-[12.5px] font-medium">
                  {p.name}
                  <div className="text-[11px] text-muted-foreground font-normal">{p.email}</div>
                </TableCell>
                <TableCell className="text-[12px]">{p.title || "—"}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{p.department || "—"}</TableCell>
                <TableCell className="text-[12px]">{p.role}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{p.started_on}</TableCell>
                <TableCell><span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", LIFECYCLE_CLS[p.lifecycle])}>{p.lifecycle}</span></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[12.5px] text-muted-foreground py-8" data-testid="compliance-people-empty">No people match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AddPersonModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
      {selected && (
        <PersonPoliciesDrawer person={selected} isAdmin={isAdmin} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
}
